import { useState, useMemo } from "react";
import { formatINR, formatNumber } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";

const GRADES = {
  basic:    { label: "Basic",    rate: 1400, desc: "Standard finishes, single-storey load" },
  standard: { label: "Standard", rate: 1800, desc: "Vitrified tiles, standard fittings" },
  premium:  { label: "Premium",  rate: 2400, desc: "Modular kitchen, imported hardware" },
  luxury:   { label: "Luxury",   rate: 3200, desc: "Full RCC, imported finishes, HVAC" },
};

export default function ConstructionCost() {
  const [area, setArea] = useState("2000");
  const [grade, setGrade] = useState("standard");

  const { cost, ratePerSqft } = useMemo(() => {
    const A = Number(area) || 0;
    const g = GRADES[grade];
    return { cost: A * g.rate, ratePerSqft: g.rate };
  }, [area, grade]);

  const rows = [
    ["Built-up Area", `${formatNumber(Number(area)||0)} sq ft`],
    ["Grade", GRADES[grade].label],
    ["Rate per sq ft", formatINR(ratePerSqft)],
    ["Estimated Cost", formatINR(cost)],
  ];

  return (
    <div>
      <CalcPageHeader title="Construction Cost Estimator" description="Ball-park build cost by grade for residential and small commercial projects in India." slug="construction" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <Field label="Built-up Area (sq ft)"><input data-testid="const-area-input" type="number" value={area} onChange={(e)=>setArea(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <Field label="Construction grade">
              <div className="grid grid-cols-2 gap-2" data-testid="const-grade-group">
                {Object.entries(GRADES).map(([k, v]) => (
                  <button
                    key={k}
                    data-testid={`grade-${k}`}
                    onClick={()=>setGrade(k)}
                    className={`p-3 rounded-md text-left border transition ${grade===k ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-accent"}`}
                  >
                    <div className="flex items-baseline justify-between"><span className="font-semibold text-sm">{v.label}</span><span className="stat-num text-xs text-muted-foreground">₹{v.rate}/ft²</span></div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{v.desc}</div>
                  </button>
                ))}
              </div>
            </Field>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel title="Construction Cost" slug="construction" highlight={{ label: "Estimated Cost", value: formatINR(cost) }} rows={rows} note="Rates are indicative all-India ranges (materials + labour). Excludes plot cost, interiors, statutory approvals and taxes." onReset={()=>{setArea("");setGrade("standard");}} />
        </div>
      </div>
    </div>
  );
}
